import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import GB from "country-flag-icons/react/3x2/GB";
import DE from "country-flag-icons/react/3x2/DE";

/**
 * LanguageSelector - A reusable language toggle component
 * Allows switching between English (EN) and German (DE)
 * Can switch by clicking the toggle or the language labels
 */
export default function LanguageSelector({ className = "" }) {
  const { i18n } = useTranslation();
  const language = i18n.language;

  const switchLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const toggleLanguage = () => {
    const newLang = language === "en" ? "de" : "en";
    switchLanguage(newLang);
  };

  return (
    <div
      className={`flex items-center gap-2 scale-90 sm:scale-100 ${className}`}
    >
      {/* Left Label: EN */}
      <span
        onClick={() => switchLanguage("en")}
        className={`text-sm font-bold transition-colors duration-300 cursor-pointer hover:opacity-80 ${
          language === "en" ? "text-red-500" : "text-slate-300"
        }`}
        title="Switch to English"
      >
        EN
      </span>

      {/* Toggle Switch */}
      <div
        onClick={toggleLanguage}
        className="relative w-16 lg:w-0 h-9 rounded-full cursor-pointer overflow-hidden border border-slate-100/50"
        title={language === "en" ? "Switch to German" : "Switch to English"}
      >
        {/* Sliding Flag Knob */}
        <motion.div
          initial={false}
          animate={{ x: language === "en" ? 1 : 29 }}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
          className="absolute top-[0.07rem] w-8 h-8 rounded-full shadow-lg z-10 overflow-hidden border-1"
        >
          {language === "en" ? (
            <GB
              title="English"
              className="w-full h-full object-cover scale-125"
            />
          ) : (
            <DE
              title="Deutsch"
              className="w-full h-full object-cover scale-125"
            />
          )}

          {/* Subtle 3D shine on the knob */}
          <div className="absolute inset-0 bg-gradient-to-tr from-black/10 to-white/20 pointer-events-none" />
        </motion.div>
      </div>

      {/* Right Label: DE */}
      <span
        onClick={() => switchLanguage("de")}
        className={`text-sm font-bold transition-colors duration-300 cursor-pointer hover:opacity-80 ${
          language === "de" ? "text-red-500" : "text-slate-300"
        }`}
        title="Zu Deutsch wechseln"
      >
        DE
      </span>
    </div>
  );
}
